import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { logout } from '../auth/authSlice';
import { fetchAssignments } from '../../services/assignments';
import type { Assignment, AssignmentFilter } from '../../types/Assignment';

type AssignmentsStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface AssignmentsState {
  // `null` = not yet determined. The widget stays hidden until this is `true`.
  enabled: boolean | null;
  items: Assignment[];
  status: AssignmentsStatus;
  error: string | null;
  // Filter + paging reflect the most recent fetch so "load more" can continue
  // from where it left off.
  filter: AssignmentFilter;
  page: number;
  hasMore: boolean;
}

const initialState: AssignmentsState = {
  enabled: null,
  items: [],
  status: 'idle',
  error: null,
  // Completed assignments stay inline in the list (marked on the row), so the
  // widget always fetches everything.
  filter: 'all',
  page: 1,
  hasMore: false,
};

interface LoadAssignmentsArgs {
  filter?: AssignmentFilter;
  page?: number;
  limit?: number;
}

/**
 * Upsert `incoming` into `existing` by id: existing rows are replaced in place
 * (so statuses refresh) and genuinely new rows are appended. Display ordering is
 * handled downstream by the partition utils, so source order here only needs to
 * be stable.
 */
const mergeAssignments = (
  existing: Assignment[],
  incoming: Assignment[]
): Assignment[] => {
  const incomingById = new Map(incoming.map((item) => [item.id, item]));
  const merged = existing.map((item) => incomingById.get(item.id) ?? item);
  const existingIds = new Set(existing.map((item) => item.id));

  incoming.forEach((item) => {
    if (!existingIds.has(item.id)) {
      merged.push(item);
    }
  });

  return merged;
};

export const loadAssignments = createAsyncThunk<
  {
    enabled: boolean;
    items: Assignment[];
    filter: AssignmentFilter;
    page: number;
    hasMore: boolean;
  },
  LoadAssignmentsArgs | void,
  { state: RootState }
>('assignments/loadAssignments', async (args) => {
  const filter = args?.filter ?? 'all';
  const page = args?.page ?? 1;
  const limit = args?.limit;
  const response = await fetchAssignments({ filter, page, limit });

  return {
    enabled: response.enabled,
    items: response.assignments,
    filter,
    page: response.meta.page,
    hasMore: response.meta.hasMore,
  };
});

const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    clearAssignments: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAssignments.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadAssignments.fulfilled, (state, action) => {
        const { enabled, items, filter, page, hasMore } = action.payload;

        state.status = 'succeeded';
        state.error = null;
        state.enabled = enabled;
        state.filter = filter;
        state.page = page;
        state.hasMore = hasMore;

        // Page 1 is a fresh load/refresh -> replace. Later pages append.
        state.items = page <= 1 ? items : mergeAssignments(state.items, items);
      })
      .addCase(loadAssignments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? 'Failed to load assignments';
      })
      .addCase(logout, () => initialState);
  },
});

export const { clearAssignments } = assignmentsSlice.actions;

// Selectors
export const selectAssignmentsEnabled = (state: RootState) =>
  state.assignments.enabled;
export const selectAssignmentsItems = (state: RootState) =>
  state.assignments.items;
export const selectAssignmentsStatus = (state: RootState) =>
  state.assignments.status;
export const selectAssignmentsFilter = (state: RootState) =>
  state.assignments.filter;
export const selectAssignmentsPage = (state: RootState) =>
  state.assignments.page;
export const selectAssignmentsHasMore = (state: RootState) =>
  state.assignments.hasMore;

export default assignmentsSlice.reducer;
